import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Save } from 'lucide-react';
import { handleMobileInput, validateMobile } from '@/utils/inputValidation';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const COUNTRY_CODES = ['+91', '+1', '+44', '+61', '+65', '+971', '+974', '+966', '+60', '+62', '+63', '+66', '+84', '+880', '+94'];

const Field = ({ label, required, children, hint }) => (
  <div className="flex items-start gap-6 py-3 border-b border-slate-100 last:border-0">
    <label className="w-48 flex-shrink-0 text-sm font-medium text-slate-600 pt-2">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <div className="flex-1 min-w-0">
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  </div>
);

const ReadOnlyField = ({ label, value }) => (
  <div className="flex items-start gap-6 py-3 border-b border-slate-100 last:border-0">
    <span className="w-48 flex-shrink-0 text-sm font-medium text-slate-600 pt-1">{label}</span>
    <div className="flex-1 flex items-center gap-2">
      <span className="text-sm text-slate-500 bg-slate-50 rounded-md px-3 py-1.5 border border-slate-200 w-full">{value || '—'}</span>
    </div>
  </div>
);

export default function ProfileEditPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromDashboard = searchParams.get('from') === 'dashboard';
  const [form, setForm] = useState({ full_name: '', company_name: '' });
  const [fixed, setFixed] = useState({ email: '', role: '', mobile: '', mobile_country_code: '+91' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/partner/profile`).then(res => {
      const d = res.data;
      setForm({ full_name: d.full_name || '', company_name: d.company_name || '' });
      setFixed({
        email: d.email || '',
        role: d.role === 'reseller' ? 'Synergy Partner' : d.role === 'sub_reseller' ? 'Channel Partner' : d.role || '',
        mobile: d.mobile || '',
        mobile_country_code: d.mobile_country_code || '+91',
      });
    }).catch(() => toast.error('Failed to load profile'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!form.full_name.trim()) { toast.error('Full name is required'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/partner/profile`, {
        account_name: form.full_name,
        ...(form.company_name.trim() ? { company_name: form.company_name.trim() } : {}),
      });
      toast.success('Profile saved');
      navigate(fromDashboard ? '/reseller/profile' : '/partner/kyc-portal?tab=profile');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4">
        <button
          onClick={() => navigate(fromDashboard ? '/reseller/profile' : '/partner/kyc-portal?tab=profile')}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors"
          data-testid="back-to-profile"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="h-5 w-px bg-slate-200" />
        <h1 className="text-lg font-semibold text-slate-800">Edit Profile</h1>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
            <p className="text-sm text-slate-600">Update your personal profile information.</p>
            <p className="text-xs text-slate-400 mt-0.5">Email, Role, and Mobile Number cannot be changed. Company Name updates your storefront.</p>
          </div>
          <div className="px-6 py-4">
            <ReadOnlyField label="Email" value={fixed.email} />
            <ReadOnlyField label="Role" value={fixed.role} />

            <Field label="Full Name" required>
              <Input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                placeholder="Your full name" data-testid="edit-full-name" />
            </Field>

            <Field label="Company Name" hint="Updates your white-label storefront name">
              <Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
                placeholder="Your company name" data-testid="edit-company-name" />
            </Field>

            {/* Mobile — read-only, admin-only */}
            <div className="flex items-start gap-6 py-3 border-b border-slate-100">
              <label className="w-48 flex-shrink-0 text-sm font-medium text-slate-600 pt-2">Mobile Number</label>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 bg-slate-50 rounded-md px-3 py-1.5 border border-slate-200 flex-1">
                    {`${fixed.mobile_country_code} ${fixed.mobile}`.trim() || '—'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
            <Button variant="outline" onClick={() => navigate(fromDashboard ? '/reseller/profile' : '/partner/kyc-portal?tab=profile')} data-testid="cancel-profile-edit">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-profile">
              {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
